import React from 'react';
import { spacer12, spacer20, Text, VisuallyHidden } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import { DocumentLink } from 'lib/sanity/types';
import { useTranslation } from 'providers/translations/TranslationProvider';
import IconChevron from 'components/IconChevron';
import Ellipsis from 'components/Ellipsis';
import { ArticleLink } from 'components/Links';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';
import { Whats } from 'utils/metrics';
import useShouldUseSemanticSearch from 'utils/semantic';
import List from 'components/List';

interface SearchResultsProps {
  searchResults: DocumentLink[];
  ubi: UbiHomePage;
}

const Container = styled.div`
  padding-top: ${spacer12};
`;

// Exported for quicklinks-in-search experiment
export const StyledArticleLink = styled(ArticleLink)`
  display: flex;
  flex: 1;
  justify-content: space-between;
  width: 100%;
`;

// Exported for quicklinks-in-search experiment
export const ListItem = styled(Text).attrs({ as: 'li' })`
  height: 24px;
  padding: ${spacer20} 0;
  display: flex;
  align-items: center;
`;

const SearchResults = ({ searchResults, ubi }: SearchResultsProps) => {
  const { t } = useTranslation();
  const shouldUseSemanticSearch = useShouldUseSemanticSearch();
  const emptyResultCopy = shouldUseSemanticSearch
    ? t('semantic_search_results_no_results_description')
    : t('search_results_no_results_description');

  return (
    <Container aria-label={t('search_results')}>
      {!searchResults.length && (
        <Text as="span" aria-live="polite" aria-atomic="true" data-testid="noResultsMessage" variant="bodyMedium">
          <em>{emptyResultCopy}</em>
        </Text>
      )}

      {searchResults.length > 0 && (
        <>
          <VisuallyHidden data-testid="ariaMessage" role="status" aria-live="polite" aria-atomic="true">
            {searchResults.length} {t('search_results_found')}
          </VisuallyHidden>
          <List data-testid="resultList">
            {searchResults.map((article, index) => (
              <ListItem key={article.slug} role="listitem" variant="bodyMedium">
                <StyledArticleLink
                  data-testid={`${article.title}-Link`}
                  slug={article.slug}
                  eventDefinition={{
                    category: 'search-dropdown',
                    action: `click-index-${index + 1}`,
                    label: article.slug,
                  }}
                  ubiFactory={ubi.logSearchResult(article.slug, index)}
                  sendMetrics={{
                    what: Whats.SEARCH_DROPDOWN,
                    tags: {
                      slug: article.slug,
                      engine: 'sanity',
                    },
                  }}
                >
                  <Ellipsis variant="bodyMedium">{article.title}</Ellipsis>
                  <IconChevron />
                </StyledArticleLink>
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Container>
  );
};

export default SearchResults;
